import React, { useEffect, useState } from 'react'
import { Box, ButtonBase, IconButton, Tooltip, Typography, useTheme, useMediaQuery } from '@mui/material'
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined'
import Diversity3OutlinedIcon from '@mui/icons-material/Diversity3Outlined'
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined'
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined'
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  WorkspacesIcon,
  AnalyticsIcon,
  SettingsIcon,
} from '../icons/TempIcons'
import { colors, spacing, getThemeColor, shell } from '../../design-system/tokens'
import { createTypographyStyle } from '../../design-system/tokens/typography'
import { useWhiteLabel } from '../../hooks/useWhiteLabel'
import { useOrgStore } from '../../store/orgStore'
import { organizationApi } from '../../services/organizationApi'
import { isFeedbackAllowed, isFeedbackOpsAllowed } from '../feedback/useFeedbackGate'
import { FeedbackWidget } from '../feedback/FeedbackWidget'
import { useAuth } from '../../hooks/useAuth'

const WorkbenchIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="3" />
    <path d="M9 4V20" />
    <path d="M13 9H18" />
    <path d="M13 13H18" />
    <path d="M13 17H16.5" />
    <path d="M5.8 8.5H6.2" />
    <path d="M5.8 12H6.2" />
    <path d="M5.8 15.5H6.2" />
  </svg>
)

interface GlobalNavSidebarProps {
  compact?: boolean
  activeSection?: 'workspaces' | 'estate' | 'analytics' | 'agent' | 'settings' | 'team' | 'knowledge-base' | 'feedback-submit' | 'feedback' | 'feedback-ops' | 'deployments'
  onNavigate?: (section: string) => void
}

interface NavItem {
  id: string
  label: string
  icon: React.ReactNode
  path?: string
  onClick?: () => void
}

const GlobalNavSidebar: React.FC<GlobalNavSidebarProps> = ({
  activeSection,
  onNavigate,
  compact = false,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const location = useLocation()
  const navigate = useNavigate()
  const { whiteLabelSettings, isWhiteLabelEnabled } = useWhiteLabel()
  const { activeOrg, setActiveOrg } = useOrgStore()
  const { user, isAuthenticated } = useAuth()
  const showFeedbackLink = isFeedbackAllowed(user, isAuthenticated)
  const showFeedbackOpsLink = isFeedbackOpsAllowed(user, isAuthenticated)
  const [feedbackOpenSignal, setFeedbackOpenSignal] = useState(0)

  // Auto-heal: if the persisted activeOrg is missing memberRole (corrupted by a bug),
  // re-fetch the org list and restore the correct role from the server.
  useEffect(() => {
    if (activeOrg && !activeOrg.memberRole) {
      organizationApi.listMyOrgs().then((orgs) => {
        const fresh = orgs.find((o) => o.id === activeOrg.id)
        if (fresh) setActiveOrg(fresh)
      }).catch(() => {})
    }
  }, [activeOrg?.id, activeOrg?.memberRole])

  const navItems: NavItem[] = [
    {
      id: 'workspaces',
      label: 'Programs',
      icon: <WorkspacesIcon />,
      path: '/workspaces',
    },
    {
      id: 'estate',
      label: 'Database sources',
      icon: <StorageOutlinedIcon />,
      path: '/estate',
    },
    {
      id: 'analytics',
      label: 'Usage',
      icon: <AnalyticsIcon />,
      path: '/analytics',
    },
    {
      id: 'agent',
      label: 'Workbench',
      icon: <WorkbenchIcon />,
      path: '/ai-canvas',
    },
    {
      id: 'knowledge-base',
      label: 'Docs',
      icon: <MenuBookOutlinedIcon />,
      path: '/knowledge-base',
    },
    {
      id: 'team',
      label: 'Meet the Team',
      icon: <Diversity3OutlinedIcon />,
      path: '/meet-the-team',
    },
  ]

  // Settings nav item (only for org owners/admins)
  const settingsItem: NavItem | null = activeOrg && (activeOrg.memberRole === 'OWNER' || activeOrg.memberRole === 'ADMIN')
    ? { id: 'settings', label: 'Org Settings', icon: <SettingsIcon />, path: '/org-settings' }
    : null

  const feedbackSubmitItem: NavItem | null = showFeedbackLink
    ? {
        id: 'feedback-submit',
        label: 'Send Feedback',
        icon: <FeedbackOutlinedIcon />,
        onClick: () => setFeedbackOpenSignal((signal) => signal + 1),
      }
    : null

  const feedbackMineItem: NavItem | null = showFeedbackLink
    ? { id: 'feedback', label: 'My Feedback', icon: <ListAltOutlinedIcon />, path: '/feedback/mine' }
    : null

  const feedbackOpsItem: NavItem | null = showFeedbackOpsLink
    ? { id: 'feedback-ops', label: 'Feedback Ops', icon: <FactCheckOutlinedIcon />, path: '/feedback/ops' }
    : null

  const deploymentsItem: NavItem | null = showFeedbackOpsLink
    ? { id: 'deployments', label: 'Deployments', icon: <CloudUploadOutlinedIcon />, path: '/deployments' }
    : null

  const allNavItems = [
    ...navItems,
    ...(settingsItem ? [settingsItem] : []),
    ...(feedbackSubmitItem ? [feedbackSubmitItem] : []),
    ...(feedbackMineItem ? [feedbackMineItem] : []),
    ...(feedbackOpsItem ? [feedbackOpsItem] : []),
    ...(deploymentsItem ? [deploymentsItem] : []),
  ]

  const handleNavClick = (item: NavItem) => {
    if (item.onClick) {
      item.onClick()
      return
    }
    if (!item.path) return
    if (onNavigate) {
      onNavigate(item.id)
    } else {
      navigate(item.path)
    }
  }

  const isActive = (item: NavItem) => {
    if (activeSection) {
      return activeSection === item.id
    }
    return item.path ? location.pathname.startsWith(item.path) : false
  }

  const feedbackDialog = showFeedbackLink
    ? <FeedbackWidget launcher="none" openSignal={feedbackOpenSignal} />
    : null

  if (isMobile) {
    return (
      <>
        {feedbackDialog}
        <Box
          component="nav"
          aria-label="Main navigation"
          sx={{
            position: 'fixed',
            bottom: 'var(--archer-shell-footer-height, 0px)',
            left: 0,
            right: 0,
            height: `${shell.rail.mobileHeight}px`,
            backgroundColor: getThemeColor('background.card'),
            borderTop: `1px solid ${getThemeColor('border.subtle')}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            overflowX: 'auto',
            overflowY: 'hidden',
            zIndex: theme.zIndex.drawer,
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
        >
          {allNavItems.map((item) => {
            const active = isActive(item)
            return (
              <ButtonBase
                key={item.id}
                aria-label={item.label}
                onClick={() => handleNavClick(item)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '2px',
                  minWidth: shell.rail.mobileNavItemMinWidth,
                  px: 1.5,
                  flexShrink: 0,
                  minHeight: shell.rail.mobileNavItemMinHeight,
                  cursor: 'pointer',
                  color: active ? colors.accent.main : colors.text.secondary,
                  transition: 'color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '& svg': { width: 22, height: 22 },
                  '&:active': { transform: 'scale(0.92)' },
                  '&:focus-visible': { outline: `2px solid ${colors.accent.main}`, outlineOffset: -2 },
                }}
              >
                {item.icon}
                <Typography sx={{
                  ...createTypographyStyle('caption'),
                  fontWeight: active ? 600 : 400,
                  whiteSpace: 'nowrap',
                }}>
                  {item.label}
                </Typography>
              </ButtonBase>
            )
          })}
        </Box>
      </>
    )
  }

  return (
    <>
      {feedbackDialog}
      <Box
        component="nav"
        aria-label="Main navigation"
        sx={{
          width: compact ? 56 : shell.rail.desktopWidth,
          height: '100vh',
          background: shell.surfaces.railBackground,
          borderRight: `1px solid ${colors.border.primary}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'fixed',
          left: 0,
          top: 0,
          zIndex: theme.zIndex.drawer,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: `${shell.rail.brandHeight}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            borderBottom: `1px solid ${colors.border.primary}`,
            background: shell.surfaces.railBrandBackground,
          }}
        >
          {isWhiteLabelEnabled && whiteLabelSettings.customerLogo ? (
            <img
              src={whiteLabelSettings.customerLogo}
              alt={whiteLabelSettings.companyName}
              style={{ width: `${shell.rail.logoSize}px`, height: `${shell.rail.logoSize}px`, objectFit: 'contain' }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
            />
          ) : (
            <img
              src={compact ? '/archer_icon.svg' : '/archer_wordmark.svg'}
              alt="Archer"
              style={{ width: compact ? 26 : shell.rail.wordmarkWidth, height: compact ? 26 : shell.rail.wordmarkHeight, objectFit: 'contain' }}
            />
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: compact ? 0.75 : spacing.scale[1],
            pt: compact ? 1.5 : spacing.scale[3],
            pb: 5,
            minHeight: 0,
            overflowY: 'auto',
            width: '100%',
          }}
        >
          {allNavItems.map((item) => {
            const active = isActive(item)
            return (
              <Tooltip key={item.id} title={item.label} placement="right" arrow>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    '&::before': active
                      ? {
                          content: '""',
                          position: 'absolute',
                          left: shell.rail.indicatorInset,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: shell.rail.indicatorWidth,
                          height: shell.rail.indicatorHeight,
                          borderRadius: 999,
                          backgroundColor: colors.accent.main,
                        }
                      : {},
                  }}
                >
                  <IconButton
                    aria-label={item.label}
                    onClick={() => handleNavClick(item)}
                    sx={{
                      width: compact ? 40 : shell.rail.navButtonSize,
                      height: compact ? 40 : shell.rail.navButtonSize,
                      borderRadius: spacing.borderRadius.lg,
                      color: active
                        ? colors.text.onPrimary
                        : colors.text.secondary,
                      backgroundColor: active
                        ? colors.accent.main
                        : 'transparent',
                      transition: `all 0.2s cubic-bezier(0.4, 0, 0.2, 1)`,
                      '& svg': { width: 22, height: 22 },
                      '&:hover': {
                        backgroundColor: active
                          ? colors.accent.main
                          : colors.neutral[100],
                        transform: 'scale(1.04)',
                      },
                      '&:active': {
                        transform: 'scale(0.96)',
                      },
                    }}
                  >
                    {item.icon}
                  </IconButton>
                </Box>
              </Tooltip>
            )
          })}
        </Box>
      </Box>
    </>
  )
}

export default GlobalNavSidebar
